import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Plus, GitBranch, MessageSquare, PenTool, ScanLine, Receipt,
  DollarSign, Sparkles, Loader2, Send,
} from "lucide-react";

const ICONS = {
  "plus": Plus,
  "git-branch": GitBranch,
  "message-square": MessageSquare,
  "pen-tool": PenTool,
  "scan-line": ScanLine,
  "receipt": Receipt,
  "dollar-sign": DollarSign,
};

const KIND_COLORS = {
  created: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  status: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
  comment: "border-slate-700 bg-slate-800/40 text-slate-200",
  signature: "border-purple-500/40 bg-purple-500/10 text-purple-300",
  scan: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  invoice: "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
  payment: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
};

export default function JobTimeline({ jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/jobs/${jobId}/timeline`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (jobId) load(); }, [jobId]);

  const postComment = async () => {
    const body = comment.trim();
    if (!body) return;
    setPosting(true);
    try {
      await api.post(`/jobs/${jobId}/comments`, { body });
      setComment("");
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error("Failed to post note");
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-8 flex items-center justify-center text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading timeline…
      </div>
    );
  }

  const events = data?.events || [];

  return (
    <div className="space-y-4" data-testid="job-timeline">
      {/* Comment box */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Add a note</div>
        <div className="flex gap-2">
          <Textarea
            data-testid="timeline-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Drop a note for the team (visible on this job's timeline)…"
            className="bg-[#030712] border-slate-800 min-h-[60px]"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) postComment(); }}
          />
          <Button
            data-testid="timeline-post-btn"
            onClick={postComment}
            disabled={posting || !comment.trim()}
            className="bg-blue-600 hover:bg-blue-500 shrink-0 h-auto"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {posting ? "Posting…" : "Post"}
          </Button>
        </div>
        <div className="text-[10px] text-slate-600 mt-1.5">Tip: ⌘+Enter to submit</div>
      </div>

      {/* Events feed */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Activity</div>
            <h3 className="font-display text-xl font-medium mt-1">Job Timeline</h3>
          </div>
          <div className="text-xs text-slate-500 font-mono">{events.length} event{events.length !== 1 ? "s" : ""}</div>
        </div>

        {events.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-10">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No activity yet. Updating the job will populate events here.
          </div>
        ) : (
          <div className="relative pl-6">
            <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-800" />
            <div className="space-y-3">
              {[...events].reverse().map((ev, i) => {
                const Icon = ICONS[ev.icon] || GitBranch;
                const color = KIND_COLORS[ev.kind] || KIND_COLORS.comment;
                return (
                  <div key={i} data-testid={`timeline-event-${i}`} className="relative">
                    <div className={`absolute -left-[18px] top-1.5 w-3 h-3 rounded-full border-2 border-[#0B1120] ${color.split(" ")[1]}`} />
                    <div className={`rounded-lg border p-3 ${color}`}>
                      <div className="flex items-start gap-2.5">
                        <Icon className="w-4 h-4 mt-0.5 shrink-0 opacity-80" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <div className="text-sm font-medium">{ev.title}</div>
                            <div className="text-[10px] font-mono opacity-60">
                              {ev.at ? new Date(ev.at).toLocaleString() : "—"}
                            </div>
                          </div>
                          {ev.detail && <div className="text-xs opacity-80 mt-0.5 break-words">{ev.detail}</div>}
                          {ev.author && ev.kind === "comment" && (
                            <div className="text-[10px] uppercase tracking-wider opacity-50 mt-1.5">{ev.author}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
